import { useState, useCallback, useEffect } from 'react';
import { api } from '../utils/api';

export interface BusinessConfig {
  name: string;
  address: string;
  phone: string;
  nit: string;         // NIT / RUT / identificación fiscal
  slogan: string;
  receiptFooter: string;
  logoUrl: string;     // URL pública o data-URL de la imagen
  gpsEnabled?: boolean;
  latitude?: string;
  longitude?: string;
  maxDistance?: string;
  targetFoodCostPct?: number;
  isKitchenOnline?: boolean;
  lastHeartbeat?: string;
}

const STORAGE_KEY = 'business_config';

const DEFAULTS: BusinessConfig = {
  name: localStorage.getItem('restaurant_name') || 'Mi Restaurante',
  address: '',
  phone: '',
  nit: '',
  slogan: '',
  receiptFooter: '¡Gracias por su visita! Vuelva pronto.',
  logoUrl: '',
  gpsEnabled: false,
  latitude: '',
  longitude: '',
  maxDistance: '100',
  targetFoodCostPct: 30,
};

function loadLocal(): BusinessConfig {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch {
    // ignore
  }
  return DEFAULTS;
}

export function saveLocalConfig(config: BusinessConfig) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  localStorage.setItem('restaurant_name', config.name);
}

export function getBusinessConfig(): BusinessConfig {
  return loadLocal();
}

export function useBusinessConfig() {
  const [config, setConfigState] = useState<BusinessConfig>(loadLocal);
  const [loading, setLoading] = useState(true);

  // Fetch configuration from database on mount
  useEffect(() => {
    let active = true;
    api.get<BusinessConfig>('/business-config')
      .then(res => {
        if (active && res.data) {
          setConfigState(res.data);
          saveLocalConfig(res.data);
        }
      })
      .catch(err => {
        console.error('Failed to load business config from backend, using local:', err);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, []);

  const save = useCallback(async (next: BusinessConfig) => {
    setConfigState(next);
    saveLocalConfig(next);
    try {
      await api.post('/business-config', next);
    } catch (err) {
      console.error('Failed to save business config to backend:', err);
      throw err;
    }
  }, []);

  const update = useCallback(async (patch: Partial<BusinessConfig>) => {
    setConfigState(prev => {
      const next = { ...prev, ...patch };
      saveLocalConfig(next);
      api.post('/business-config', next).catch(err => {
        console.error('Failed to update business config on backend:', err);
      });
      return next;
    });
  }, []);

  return { config, save, update, loading };
}

